// frontend/src/pages/customer/Cart.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from '../../utils/axios';
import {
  FiShoppingCart,
  FiTrash2,
  FiPlus,
  FiMinus,
  FiArrowLeft,
  FiArrowRight,
  FiAlertCircle,
  FiPackage,
  FiCreditCard,
  FiDollarSign
} from 'react-icons/fi';

const Cart = () => {
  const navigate = useNavigate();
  const { user, loadCartCount } = useOutletContext();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingCheckout, setProcessingCheckout] = useState(false);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
  const [deliveryInfo, setDeliveryInfo] = useState({
    address: user?.address || '',
    phone: user?.phone || '',
    note: ''
  });

  useEffect(() => {
    loadCart();
    loadBankAccounts();
  }, []);

  const loadCart = async () => {
    try {
      const cart = JSON.parse(localStorage.getItem('customerCart') || '[]');
      
      console.log('🛒 LocalStorage cart:', cart);
      
      if (cart.length === 0) {
        console.log('⚠️ Cart is empty in localStorage');
        setCartItems([]);
        setLoading(false);
        return;
      }

      // Validate cart with backend
      const response = await axios.post('/api/customer/cart/validate', {
        items: cart
      });

      console.log('✅ Backend validation response:', response.data);

      if (response.data.success) {
        const validatedItems = response.data.data.items;
        console.log('📦 Validated items:', validatedItems);
        setCartItems(validatedItems);
      }
    } catch (error) {
      console.error('❌ Load cart error:', error);
      toast.error('Сагс ачаалахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const loadBankAccounts = async () => {
    try {
      const response = await axios.get('/api/customer/bank-accounts');
      if (response.data.success) {
        setBankAccounts(response.data.data.accounts);
        if (response.data.data.accounts.length > 0) {
          setSelectedBankAccount(response.data.data.accounts[0].id);
        }
      }
    } catch (error) {
      console.error('Load bank accounts error:', error);
    }
  };

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity < 1) return;

    const cart = JSON.parse(localStorage.getItem('customerCart') || '[]');
    const itemIndex = cart.findIndex(item => item.productId === productId);

    if (itemIndex > -1) {
      const item = cartItems.find(i => i.id === productId);
      
      if (newQuantity > item.stock_quantity) {
        toast.warning(`Зөвхөн ${item.stock_quantity} ширхэг үлдсэн байна`);
        return;
      }

      cart[itemIndex].quantity = newQuantity;
      localStorage.setItem('customerCart', JSON.stringify(cart));
      loadCart();
      loadCartCount();
    }
  };

  const removeItem = (productId) => {
    const cart = JSON.parse(localStorage.getItem('customerCart') || '[]');
    const updatedCart = cart.filter(item => item.productId !== productId);
    localStorage.setItem('customerCart', JSON.stringify(updatedCart));
    loadCart();
    loadCartCount();
    toast.success('Барааг сагснаас хаслаа');
  };

  const clearCart = () => {
    if (window.confirm('Сагсыг хоослох уу?')) {
      localStorage.removeItem('customerCart');
      setCartItems([]);
      loadCartCount();
      toast.success('Сагсыг хоослов');
    }
  };

  const calculateTotals = () => {
    let subtotal = 0;
    let totalItems = 0;

    const cart = JSON.parse(localStorage.getItem('customerCart') || '[]');

    console.log('💰 ===== CALCULATE TOTALS =====');
    console.log('👤 User customer_type:', user?.customer_type);
    console.log('🛒 Cart items:', cart);

    cartItems.forEach(item => {
      const cartItem = cart.find(c => c.productId === item.id);
      if (cartItem) {
        const quantity = cartItem.quantity;
        
        console.log(`\n📦 Product: ${item.name}`);
        console.log(`   Quantity: ${quantity}`);
        console.log(`   Wholesale qty needed: ${item.wholesale_quantity}`);
        console.log(`   Retail price: ₮${item.retail_price}`);
        console.log(`   Wholesale price: ₮${item.wholesale_price}`);
        console.log(`   Customer type: ${user?.customer_type}`);
        
        const isWholesale = user?.customer_type === 'wholesale' && quantity >= item.wholesale_quantity;
        const price = isWholesale ? item.wholesale_price : item.retail_price;
        
        console.log(`   ✅ Using ${isWholesale ? 'WHOLESALE' : 'RETAIL'} price: ₮${price}`);
        console.log(`   Subtotal: ₮${price * quantity}`);
        
        subtotal += price * quantity;
        totalItems += quantity;
      }
    });

    // ✅ Хөнгөлөлт тооцох
    const discountRate = user?.discount_rate || 0;
    const discountAmount = subtotal * (discountRate / 100);
    const afterDiscount = subtotal - discountAmount;

    // ✅ НӨАТ тооцох (tax_exempt шалгах)
    const taxExempt = user?.tax_exempt || false;
    const tax = taxExempt ? 0 : afterDiscount * 0.1;
    
    const total = afterDiscount + tax;

    console.log('\n💵 TOTALS:');
    console.log(`   Subtotal: ₮${subtotal.toLocaleString()}`);
    console.log(`   Discount (${discountRate}%): -₮${discountAmount.toLocaleString()}`);
    console.log(`   After Discount: ₮${afterDiscount.toLocaleString()}`);
    console.log(`   Tax Exempt: ${taxExempt}`);
    console.log(`   Tax: ₮${tax.toLocaleString()}`);
    console.log(`   Total: ₮${total.toLocaleString()}`);
    console.log('===============================\n');

    return { 
      subtotal, 
      discountRate,
      discountAmount,
      afterDiscount,
      tax, 
      total, 
      totalItems,
      taxExempt 
    };
  };

  const handleCheckoutClick = () => {
    if (cartItems.length === 0) {
      toast.warning('Сагс хоосон байна');
      return;
    }
    setShowCheckoutModal(true);
  };

  const handleCheckout = async () => {
    setProcessingCheckout(true);

    try {
      const cart = JSON.parse(localStorage.getItem('customerCart') || '[]');
      const orderItems = cart.map(item => {
        const product = cartItems.find(p => p.id === item.productId);
        const price = user?.customer_type === 'wholesale' && item.quantity >= product.wholesale_quantity
          ? product.wholesale_price
          : product.retail_price;

        return {
          product_id: item.productId,
          quantity: item.quantity,
          unit_price: price
        };
      });

      const totals = calculateTotals();

      const orderData = {
        items: orderItems,
        total_amount: totals.total,
        payment_method: paymentMethod,
        bank_account_id: paymentMethod === 'bank_transfer' ? selectedBankAccount : null,
        delivery_address: deliveryInfo.address,
        delivery_phone: deliveryInfo.phone,
        delivery_note: deliveryInfo.note
      };

      const response = await axios.post('/api/customer/orders', orderData);

      if (response.data.success) {
        localStorage.removeItem('customerCart');
        loadCartCount();
        toast.success('Захиалга амжилттай үүслээ!');
        
        // Navigate to order detail with transaction info
        navigate(`/customer/orders/${response.data.data.order.id}`, {
          state: { 
            showPaymentInfo: true,
            transaction: response.data.data.transaction 
          }
        });
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.message || 'Захиалга үүсгэхэд алдаа гарлаа');
    } finally {
      setProcessingCheckout(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="spinner-lg mb-4"></div>
          <p className="text-gray-600">Сагс ачаалж байна...</p>
        </div>
      </div>
    );
  }

  const totals = calculateTotals();

  return (
    <div className="container-custom py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <FiShoppingCart size={28} className="text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Миний сагс</h1>
            <p className="text-sm text-gray-600">
              {cartItems.length} бараа ({totals.totalItems} ширхэг)
            </p>
          </div>
        </div>
        <Link
          to="/customer/products"
          className="btn btn-secondary flex items-center gap-2"
        >
          <FiArrowLeft size={18} />
          <span>Үргэлжлүүлэх</span>
        </Link>
      </div>

      {cartItems.length === 0 ? (
        /* Empty Cart */
        <div className="card text-center py-16">
          <FiShoppingCart size={64} className="text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Таны сагс хоосон байна
          </h2>
          <p className="text-gray-600 mb-6">
            Барааны жагсаалт руу орж худалдан авалт хийнэ үү
          </p>
          <Link to="/customer/products" className="btn btn-primary inline-flex items-center gap-2">
            <FiPackage size={18} />
            <span>Бараа үзэх</span>
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {/* Clear Cart Button */}
            <div className="flex justify-end">
              <button
                onClick={clearCart}
                className="text-red-600 hover:text-red-700 text-sm font-medium flex items-center gap-1"
              >
                <FiTrash2 size={16} />
                Сагсыг хоослох
              </button>
            </div>

            {/* Items List */}
            {cartItems.map(item => {
              const cart = JSON.parse(localStorage.getItem('customerCart') || '[]');
              const cartItem = cart.find(c => c.productId === item.id);
              const quantity = cartItem?.quantity || 0;
              
              const isWholesale = user?.customer_type === 'wholesale' && 
                                  quantity >= item.wholesale_quantity &&
                                  item.wholesale_price > 0;
              
              const price = isWholesale ? item.wholesale_price : item.retail_price;
              const subtotal = price * quantity;

              return (
                <div key={item.id} className="card">
                  <div className="flex gap-4">
                    {/* Product Image */}
                    <Link
                      to={`/customer/products/${item.id}`}
                      className="flex-shrink-0"
                    >
                      <img
                        src={item.image_url ? `${process.env.REACT_APP_API_URL}${item.image_url}` : '/placeholder.png'}
                        alt={item.name}
                        className="w-24 h-24 object-cover rounded-lg"
                      />
                    </Link>

                    {/* Product Info */}
                    <div className="flex-1 min-w-0">
                      <Link
                        to={`/customer/products/${item.id}`}
                        className="font-semibold text-gray-900 hover:text-blue-600 block mb-1"
                      >
                        {item.name}
                      </Link>
                      <p className="text-sm text-gray-500 mb-2">
                        Барааны код: {item.barcode}
                      </p>

                      {/* Price Info */}
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-lg font-bold text-blue-600">
                          ₮{price.toLocaleString()}
                        </span>
                        {isWholesale && (
                          <span className="badge badge-blue text-xs">
                            Бөөний үнэ
                          </span>
                        )}
                      </div>

                      {/* Stock Warning */}
                      {item.stock_quantity < 10 && (
                        <div className="flex items-center gap-1 text-orange-600 text-sm mb-2">
                          <FiAlertCircle size={14} />
                          <span>Зөвхөн {item.stock_quantity} ширхэг үлдсэн</span>
                        </div>
                      )}

                      {/* Quantity Controls */}
                      <div className="flex items-center gap-4">
                        <div className="flex items-center border border-gray-300 rounded-lg">
                          <button
                            onClick={() => updateQuantity(item.id, quantity - 1)}
                            disabled={quantity <= 1}
                            className="p-2 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <FiMinus size={16} />
                          </button>
                          <input
                            type="number"
                            value={quantity}
                            onChange={(e) => updateQuantity(item.id, parseInt(e.target.value) || 1)}
                            className="w-16 text-center border-x border-gray-300 py-1"
                            min="1"
                            max={item.stock_quantity}
                          />
                          <button
                            onClick={() => updateQuantity(item.id, quantity + 1)}
                            disabled={quantity >= item.stock_quantity}
                            className="p-2 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <FiPlus size={16} />
                          </button>
                        </div>

                        <div className="text-sm text-gray-600">
                          × ₮{price.toLocaleString()} = <span className="font-semibold text-gray-900">₮{subtotal.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>

                    {/* Remove Button */}
                    <button
                      onClick={() => removeItem(item.id)}
                      className="flex-shrink-0 text-red-600 hover:text-red-700 p-2"
                      title="Устгах"
                    >
                      <FiTrash2 size={20} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="card sticky top-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Захиалгын дүн
              </h3>

              <div className="space-y-3 mb-4 pb-4 border-b border-gray-200">
                <div className="flex justify-between text-gray-600">
                  <span>Нийт бараа:</span>
                  <span>{totals.totalItems} ширхэг</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Дүн:</span>
                  <span className="font-semibold">₮{totals.subtotal.toLocaleString()}</span>
                </div>
                
                {/* ✅ Хөнгөлөлт */}
                {totals.discountRate > 0 && (
                  <>
                    <div className="flex justify-between text-green-600">
                      <span>Хөнгөлөлт ({totals.discountRate}%):</span>
                      <span className="font-semibold">-₮{totals.discountAmount.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-gray-600">
                      <span>Хөнгөлөлтийн дараа:</span>
                      <span className="font-semibold">₮{totals.afterDiscount.toLocaleString()}</span>
                    </div>
                  </>
                )}
                
                {/* ✅ НӨАТ - Сайжруулсан */}
                {totals.taxExempt ? (
                  <div className="flex justify-between items-center bg-green-50 -mx-3 px-3 py-2 rounded">
                    <span className="text-gray-700">НӨАТ:</span>
                    <div className="flex items-center gap-2">
                      <span className="text-green-600 font-semibold">₮0</span>
                      <span className="text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                        Чөлөөлөгдсөн
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-between text-gray-600">
                    <span>НӨАТ (10%):</span>
                    <span className="font-semibold">₮{totals.tax.toLocaleString()}</span>
                  </div>
                )}
              </div>

              <div className="flex justify-between text-lg font-bold text-gray-900 mb-6">
                <span>Нийт дүн:</span>
                <span className="text-blue-600">₮{totals.total.toLocaleString()}</span>
              </div>

              {/* Credit Limit Info */}
              {user?.credit_limit > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-blue-900 font-medium mb-1">
                    Зээлийн лимит
                  </p>
                  <p className="text-xs text-blue-700">
                    Боломжит: ₮{user.credit_limit.toLocaleString()}
                  </p>
                </div>
              )}

              {/* Checkout Button */}
              <button
                onClick={handleCheckoutClick}
                className="btn btn-primary w-full flex items-center justify-center gap-2"
              >
                <span>Захиалга өгөх</span>
                <FiArrowRight size={18} />
              </button>

              <p className="text-xs text-gray-500 text-center mt-3">
                Захиалга өгснөөр үйлчилгээний нөхцөлтэй зөвшөөрч байна
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Checkout Modal */}
      {showCheckoutModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Захиалга баталгаажуулах
              </h2>

              {/* Payment Method */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Төлбөрийн хэлбэр
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setPaymentMethod('bank_transfer')}
                    className={`p-4 border-2 rounded-lg flex items-center gap-3 transition-colors ${
                      paymentMethod === 'bank_transfer'
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <FiCreditCard size={24} className={paymentMethod === 'bank_transfer' ? 'text-blue-600' : 'text-gray-400'} />
                    <div className="text-left">
                      <p className="font-semibold text-gray-900">Шилжүүлэг</p>
                      <p className="text-xs text-gray-500">Банкны данс руу</p>
                    </div>
                  </button>
                  <button
                    onClick={() => setPaymentMethod('credit')}
                    className={`p-4 border-2 rounded-lg flex items-center gap-3 transition-colors ${
                      paymentMethod === 'credit'
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    disabled={!user?.credit_limit || user.credit_limit === 0}
                  >
                    <FiDollarSign size={24} className={paymentMethod === 'credit' ? 'text-blue-600' : 'text-gray-400'} />
                    <div className="text-left">
                      <p className="font-semibold text-gray-900">Зээлээр</p>
                      <p className="text-xs text-gray-500">
                        {user?.credit_limit > 0 ? `₮${user.credit_limit.toLocaleString()}` : 'Боломжгүй'}
                      </p>
                    </div>
                  </button>
                </div>
              </div>

              {/* Bank Account Selection */}
              {paymentMethod === 'bank_transfer' && bankAccounts.length > 0 && (
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Банкны данс сонгох
                  </label>
                  <div className="space-y-2">
                    {bankAccounts.map(account => (
                      <label
                        key={account.id}
                        className={`block p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                          selectedBankAccount === account.id
                            ? 'border-blue-600 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="bank_account"
                          value={account.id}
                          checked={selectedBankAccount === account.id}
                          onChange={(e) => setSelectedBankAccount(parseInt(e.target.value))}
                          className="sr-only"
                        />
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-semibold text-gray-900">{account.bank_name}</p>
                            <p className="text-sm text-gray-600">{account.account_number}</p>
                            <p className="text-sm text-gray-500">{account.account_name}</p>
                          </div>
                          {selectedBankAccount === account.id && (
                            <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 12 12">
                                <path d="M10 3L4.5 8.5 2 6" stroke="currentColor" strokeWidth="2" fill="none" />
                              </svg>
                            </div>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Delivery Info */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  Хүргэлтийн мэдээлэл
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Хаяг</label>
                    <input
                      type="text"
                      value={deliveryInfo.address}
                      onChange={(e) => setDeliveryInfo({...deliveryInfo, address: e.target.value})}
                      className="input w-full"
                      placeholder="Хүргэх хаяг"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Утас</label>
                    <input
                      type="tel"
                      value={deliveryInfo.phone}
                      onChange={(e) => setDeliveryInfo({...deliveryInfo, phone: e.target.value})}
                      className="input w-full"
                      placeholder="Утасны дугаар"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Тэмдэглэл</label>
                    <textarea
                      value={deliveryInfo.note}
                      onChange={(e) => setDeliveryInfo({...deliveryInfo, note: e.target.value})}
                      className="input w-full"
                      rows="2"
                      placeholder="Нэмэлт тэмдэглэл"
                    />
                  </div>
                </div>
              </div>

              {/* Order Summary */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex justify-between text-gray-600 mb-2">
                  <span>Дүн:</span>
                  <span>₮{totals.subtotal.toLocaleString()}</span>
                </div>
                
                {/* Хөнгөлөлт */}
                {totals.discountRate > 0 && (
                  <>
                    <div className="flex justify-between text-green-600 mb-2">
                      <span>Хөнгөлөлт ({totals.discountRate}%):</span>
                      <span>-₮{totals.discountAmount.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-gray-600 mb-2">
                      <span>Хөнгөлөлтийн дараа:</span>
                      <span>₮{totals.afterDiscount.toLocaleString()}</span>
                    </div>
                  </>
                )}
                
                {/* НӨАТ */}
                {totals.taxExempt ? (
                  <div className="flex justify-between text-gray-500 mb-2">
                    <span>НӨАТ:</span>
                    <span className="text-sm italic">(Чөлөөлөгдсөн)</span>
                  </div>
                ) : (
                  <div className="flex justify-between text-gray-600 mb-2">
                    <span>НӨАТ:</span>
                    <span>₮{totals.tax.toLocaleString()}</span>
                  </div>
                )}
                
                <div className="flex justify-between text-lg font-bold text-gray-900 pt-2 border-t border-gray-300">
                  <span>Нийт:</span>
                  <span className="text-blue-600">₮{totals.total.toLocaleString()}</span>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowCheckoutModal(false)}
                  className="btn btn-secondary flex-1"
                  disabled={processingCheckout}
                >
                  Буцах
                </button>
                <button
                  onClick={handleCheckout}
                  disabled={processingCheckout}
                  className="btn btn-primary flex-1 flex items-center justify-center gap-2"
                >
                  {processingCheckout ? (
                    <>
                      <div className="spinner"></div>
                      <span>Боловсруулж байна...</span>
                    </>
                  ) : (
                    <>
                      <span>Захиалга өгөх</span>
                      <FiArrowRight size={18} />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cart;